import React from "react";
import { useQuery } from "react-query";
import api from "src/rest/api"; // Assuming this path is correct
import { twMerge } from "tailwind-merge";

export interface Plan {
  id: string;
  name: string;
  description: string;
  active: "true" | "soon";
  monthly_price: number | null;
  yearly_price: number | null;
  monthly_price_id: string | null;
  yearly_price_id: string | null;
  features?: string[];
  is_popular?: boolean;
}

// Updated props for TrainerPlansModern
interface TrainerPlansModernProps {
  interval: "month" | "year"; // Interval controlled by parent
  onCheckoutRequest: (priceId: string) => void; // Function to trigger checkout from parent
}

interface PlansList {
  monthly: Array<{
    id: string;
    name: string;
    description: string;
    amount: number; // Amount in cents
    currency: string;
  }>;
  yearly: Array<{
    id: string;
    name: string;
    description: string;
    amount: number;
    currency: string;
  }>;
}

const TrainerPlansModern: React.FC<TrainerPlansModernProps> = ({
  interval,
  onCheckoutRequest,
}) => {
  const {
    data: fetchedPlans,
    isLoading,
    error,
  } = useQuery<PlansList>("trainerPlans", () =>
    api.post<PlansList>("/trainerplans/list").then((res) => res.data),
  );

  if (isLoading) {
    return (
      <div className="min-h-80 p-10 text-center text-white">
        Carregando planos...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-80 p-10 text-center text-red-400">
        Erro ao carregar planos.
      </div>
    );
  }

  const plansToShow =
    interval === "month"
      ? fetchedPlans?.monthly || []
      : fetchedPlans?.yearly || [];

  if (!plansToShow || plansToShow.length === 0) {
    return (
      <div className="min-h-80 p-10 text-center text-zinc-300">
        Nenhum plano disponível no momento.
      </div>
    );
  }
  return (
    <div className="fade-in w-full">
      <div
        className={twMerge(
          ` ${plansToShow.length < 3 ? "md:justify-center" : ""}`,
          "flex min-h-80 w-full flex-col gap-6 overflow-x-auto pb-4 md:flex-row",
        )}
      >
        {plansToShow
          .slice()
          .sort((a, b) => (a.amount ?? Infinity) - (b.amount ?? Infinity))
          .map((plan) => (
            <div
              key={`${plan.id}-${interval}`}
              onClick={() => plan.amount && onCheckoutRequest(plan.id)}
              className="group relative flex h-80 flex-1 cursor-pointer select-none flex-col justify-between rounded-lg border border-zinc-700 bg-zinc-800 p-6 text-white shadow-lg transition-all duration-300 ease-in-out hover:shadow-2xl active:scale-95 md:hover:-translate-y-2"
              style={{
                flex: "1 0 320px",
                marginTop: "8px", // Adiciona espaço para o translateY negativo
              }}
            >
              <div>
                <h3 className="mb-2 text-center text-xl font-semibold text-white">
                  {plan.name}
                </h3>
                <p className="mb-6 h-12 text-center text-sm text-zinc-400">
                  {plan.description}
                </p>
                <div className="mb-6 text-center">
                  {plan.amount ? (
                    <>
                      <span className="text-3xl font-bold text-white">
                        R$ {(plan.amount / 100).toFixed(2)}
                      </span>
                      <span className="text-zinc-400">
                        /{interval === "month" ? "mês" : "ano"}
                      </span>
                    </>
                  ) : (
                    <span className="text-xl font-bold text-zinc-500">
                      EM BREVE
                    </span>
                  )}
                </div>
                {interval === "year" && plan.amount && (
                  <div className="mb-4 text-center">
                    <span className="rounded bg-primary-800 px-2 py-1 text-xs font-semibold text-white">
                      10% de desconto
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-auto">
                <button
                  disabled={!plan.amount}
                  className={`w-full rounded-lg px-5 py-2.5 text-center font-semibold ring-2 ring-transparent transition-all duration-300 ease-in-out focus:outline-none focus:ring-primary-400 ${
                    plan.amount
                      ? "bg-black text-white hover:bg-primary-700"
                      : "cursor-not-allowed bg-zinc-600 text-zinc-400"
                  } group-hover:bg-primary-700 group-hover:shadow-[0_0_16px_4px_theme('colors.primary.500')] group-hover:ring-primary-500`}
                >
                  {plan.amount ? "Assinar Agora" : "Em Breve"}
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default TrainerPlansModern;
